import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { KeyboardAvoidingView, Text, View } from 'react-native';
import {
  NotificationMode,
  NotificationType,
  OrderAction,
  CustomerNotificationEvent,
  UpdateCustomerRequest,
} from '@oolio-group/domain';
import {
  getFullFormattedPhoneNumber,
  useLocalization,
} from '@oolio-group/localization';
import { omit } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useCart } from '../../hooks/cart/CartProvider';
import { useCartNavigationHelper } from '../../hooks/useCartNavigationHelper/useCartNavigationHelper';
import { useCartSetActiveScreen } from '../../hooks/useCartNavigationHelper/useCartSwitcherEffect';
import { useCustomers } from '../../hooks/orders/useCustomers';
import { ScreenName } from '../../lib/enums';
import { useSyncOrderEvents } from '../../hooks/useSyncOrderEvents/useSyncOrderEvents';
import { isLocalCommsEligible } from '../../utils/localComms';
import useBehaviorSubjectState from '../../hooks/rxjs/useSubjectState';
import { sessionSubject } from '../../state/sessionObservable';
import {
  isValidPhoneNumber,
  validateEmail,
  COUNTRIES,
  DEFAULT_COUNTRY,
  Country,
} from '@oolio-group/design-common';
import { AuditLogTags } from '@oolio-group/audit-sdk';
import { logCdsEvent } from '../../actionLogging/cdsAudit';
import { getStyles } from './SendReceiptScreen.styles';
import { useTheme } from '../../hooks/useTheme';
import { ButtonText } from '../../component/Button/ButtonText';
import { ButtonIcon } from '../../component/Button/ButtonIcon';
import { InputEmail } from '../../component/Input/InputEmail';
import { InputPhone } from '../../component/Input/InputPhone';

const SendReceiptScreen: React.FC = () => {
  const { colors } = useTheme();
  const styles = useMemo(() => getStyles(colors), [colors]);

  const { order } = useCart();
  const { country } = useLocalization();
  const { navigateIdleScreen } = useCartNavigationHelper();
  useCartSetActiveScreen(ScreenName.SEND_RECEIPT);
  const { value: session } = useBehaviorSubjectState(sessionSubject);
  const {
    createCustomer,
    updateCustomer,
    getCustomerById,
    customerMaps,
    loading: customerLoading,
  } = useCustomers();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [success, setSuccess] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState<Country>(
    () => COUNTRIES.find(c => c.code === country) ?? DEFAULT_COUNTRY,
  );

  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const assignedCustomer = useMemo(
    () => customerMaps[order?.customer?.id || ''],
    [customerMaps, order?.customer?.id],
  );

  useEffect(() => {
    if (order?.customer?.id) {
      getCustomerById(order.customer.id);
    }
  }, [order?.customer?.id, getCustomerById]);

  useEffect(() => {
    if (assignedCustomer) {
      setEmail(assignedCustomer.email || '');
      setPhone(assignedCustomer.phoneNumber || '');
      const isoCountryCode = assignedCustomer.preferredAddress?.isoCountryCode;
      const savedCountry = COUNTRIES.find(c => c.code === isoCountryCode);
      if (savedCountry) {
        setPhoneCountry(savedCountry);
      }
    }
  }, [assignedCustomer]);

  const onSendSuccess = useCallback(() => {
    setSubmitting(false);
    setSuccess(true);
    logCdsEvent({
      actionType: 'RECEIPT_SEND_SUCCEEDED',
      tag: AuditLogTags.USER_ACTION,
      phrase: 'receipt sent to customer',
      orderId: order?.id,
      orderNumber: order?.orderNumber,
    });
    // Show the success tick briefly before returning to the idle screen.
    redirectTimer.current = setTimeout(navigateIdleScreen, 1500);
  }, [navigateIdleScreen, order?.id, order?.orderNumber]);

  // Cancel a pending redirect if the screen unmounts first.
  useEffect(() => {
    return () => {
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
      }
    };
  }, []);

  const onDismiss = useCallback(() => {
    logCdsEvent({
      actionType: 'RECEIPT_SCREEN_ABANDONED',
      tag: AuditLogTags.USER_ACTION,
      phrase: 'customer left the receipt screen without sending',
      orderId: order?.id,
      orderNumber: order?.orderNumber,
    });
    navigateIdleScreen();
  }, [navigateIdleScreen, order?.id, order?.orderNumber]);

  const onSendError = useCallback(() => {
    setSubmitting(false);
    setSendError(true);
    logCdsEvent({
      actionType: 'RECEIPT_SEND_FAILED',
      tag: AuditLogTags.USER_ACTION,
      phrase: 'receipt could not be sent',
      level: 'warn',
      orderId: order?.id,
      orderNumber: order?.orderNumber,
    });
  }, [order?.id, order?.orderNumber]);

  const { syncOrderEvents } = useSyncOrderEvents(onSendSuccess, onSendError);

  const trimmedEmail = email.trim();
  const trimmedPhone = phone.trim();
  const emailValid = trimmedEmail.length > 0 && validateEmail(trimmedEmail);
  const phoneValid =
    trimmedPhone.length > 0 &&
    isValidPhoneNumber(trimmedPhone, phoneCountry.code);
  const isBusy = submitting || customerLoading;
  const canSubmit = (emailValid || phoneValid) && !isBusy;

  const onSubmit = useCallback(() => {
    if (!session || !canSubmit || !order?.id) return;

    setSendError(false);
    setSuccess(false);

    const events: CustomerNotificationEvent[] = [];
    const base = {
      action: OrderAction.CUSTOMER_NOTIFICATION,
      orderId: order.id,
      organizationId: session.currentOrganization?.id as string,
      venueId: session.currentVenue?.id as string,
      storeId: session.currentStore?.id as string,
      deviceId: session.device?.id as string,
      timestamp: Date.now(),
      triggeredBy: '',
    };

    if (emailValid) {
      events.push({
        ...base,
        id: uuidv4(),
        notificationType: NotificationType.SEND_RECEIPT,
        notificationMode: NotificationMode.EMAIL,
        email: trimmedEmail.toLowerCase(),
      });
    }

    if (phoneValid) {
      events.push({
        ...base,
        id: uuidv4(),
        notificationType: NotificationType.SEND_MESSAGE,
        notificationMode: NotificationMode.PHONE,
        phone: getFullFormattedPhoneNumber(phoneCountry.code, trimmedPhone),
      });
    }

    if (events.length === 0) return;
    setSubmitting(true);
    const mode =
      emailValid && phoneValid ? 'both' : emailValid ? 'email' : 'sms';
    logCdsEvent({
      actionType: 'RECEIPT_SEND_REQUESTED',
      tag: AuditLogTags.USER_ACTION,
      phrase: `receipt requested by ${mode}`,
      orderId: order.id,
      orderNumber: order?.orderNumber,
      context: { mode, customerId: order?.customer?.id },
    });
    syncOrderEvents(events);
    // Local comms has no delivery confirmation to wait on, so report
    // success as soon as the event is handed off rather than spinning
    // indefinitely for a callback that will never arrive.
    if (isLocalCommsEligible()) {
      onSendSuccess();
    }

    const customerInput = {
      // TODO: CDS no longer collects a customer name — new customers are
      // created with empty first/last names. Revisit if name capture returns.
      firstName: assignedCustomer?.firstName ?? '',
      lastName: assignedCustomer?.lastName ?? '',
      phoneNumber: trimmedPhone,
      phone: trimmedPhone
        ? getFullFormattedPhoneNumber(phoneCountry.code, trimmedPhone)
        : '',
      email: trimmedEmail,
      preferredAddress: {
        isoCountryCode: phoneCountry.code,
      },
    };
    if (order?.customer?.id) {
      const existingPreferredAddress = omit(
        assignedCustomer?.preferredAddress || {},
        '__typename',
      );
      logCdsEvent({
        actionType: 'CUSTOMER_UPDATED',
        tag: AuditLogTags.USER_ACTION,
        phrase: 'customer details updated from CDS',
        orderId: order.id,
        orderNumber: order?.orderNumber,
        context: { customerId: order.customer.id, mode },
      });
      updateCustomer({
        ...customerInput,
        id: order.customer.id,
        preferredAddress: {
          ...existingPreferredAddress,
          isoCountryCode: phoneCountry.code,
        },
      } as UpdateCustomerRequest);
    } else {
      logCdsEvent({
        actionType: 'CUSTOMER_CREATED',
        tag: AuditLogTags.USER_ACTION,
        phrase: 'customer created from CDS',
        orderId: order.id,
        orderNumber: order?.orderNumber,
        context: { mode },
      });
      createCustomer(customerInput);
    }
  }, [
    canSubmit,
    emailValid,
    order.id,
    order?.orderNumber,
    order?.customer?.id,
    phoneValid,
    phoneCountry,
    session,
    syncOrderEvents,
    trimmedEmail,
    trimmedPhone,
    assignedCustomer,
    createCustomer,
    updateCustomer,
    onSendSuccess,
  ]);

  return (
    <View style={styles.container}>
      <ButtonIcon
        id="btn-close"
        icon="times"
        onPress={onDismiss}
        containerStyles={styles.btnClose}
      />
      <KeyboardAvoidingView behavior="padding" style={styles.content}>
        <Text style={styles.title}>Enter your details, for a receipt!</Text>
        <View style={styles.form}>
          <InputEmail
            testID="input-email"
            value={email}
            onChangeText={setEmail}
          />
          <InputPhone
            testID="input-phone"
            value={phone}
            onChangeText={setPhone}
            placeholder="Or enter phone number..."
            selectedCountry={phoneCountry}
            onCountryChange={setPhoneCountry}
          />
        </View>
        <View style={styles.actions}>
          <ButtonText
            id="btn-submit"
            label="Send Receipt"
            onPress={onSubmit}
            disabled={!canSubmit}
            loading={isBusy}
            success={success}
            buttonStyle="primary"
          />
          {sendError && (
            <Text style={styles.error}>
              Could not send the receipt, please try again!
            </Text>
          )}
        </View>
      </KeyboardAvoidingView>
    </View>
  );
};

export default SendReceiptScreen;
